import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, SpinnerGap, Wallet, Briefcase, CurrencyInr } from '@phosphor-icons/react';
import { fmtINR } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EarningsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/my-earnings`, { withCredentials: true });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;

  const placements = data?.placements || [];
  const total = data?.total_earnings || 0;

  return (
    <div className="space-y-6" data-testid="earnings-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9" data-testid="back-btn"><ArrowLeft size={20} /></Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">My Earnings</h1>
          <p className="text-slate-500 mt-1 text-sm">Incentive earnings from approved placements</p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase text-slate-500 font-semibold">Total Earnings</p>
                <p className="text-3xl font-bold text-emerald-700 mt-1" data-testid="total-earnings">{fmtINR(total)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CurrencyInr size={24} className="text-emerald-600" weight="bold" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <p className="text-xs uppercase text-slate-500 font-semibold">Placements Contributing</p>
            <p className="text-3xl font-bold text-blue-700 mt-1" data-testid="placement-count">{placements.length}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <p className="text-xs uppercase text-slate-500 font-semibold">Avg Per Placement</p>
            <p className="text-3xl font-bold text-slate-700 mt-1">{placements.length > 0 ? fmtINR(Math.round(total / placements.length)) : fmtINR(0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Placement Details */}
      <Card className="border-slate-200" data-testid="earnings-table-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
            <Briefcase size={18} className="text-blue-600" /> Earnings by Placement
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {placements.length === 0 ? (
            <div className="p-10 text-center text-slate-400">
              <Wallet size={48} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">No earnings yet</p>
              <p className="text-sm mt-1">Earnings will appear here when your approved placements generate incentive splits.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-slate-100">
                    <th className="px-4 py-2.5 text-left font-semibold">Candidate</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Module</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Category</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Your Role(s)</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Created By</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Your Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {placements.map((p) => (
                    <tr key={p.placement_id} className="hover:bg-slate-50/60 transition-colors" data-testid={`earning-row-${p.placement_id}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => navigate(`/dashboard/placements/${p.placement_id}`)} className="font-medium text-blue-700 hover:text-blue-900 underline text-left" data-testid={`earning-link-${p.placement_id}`}>
                          {p.candidate_name}
                        </button>
                        {p.organization && <p className="text-[10px] text-slate-400">{p.organization}</p>}
                      </td>
                      <td className="px-3 py-3">
                        <Badge variant="outline" className="text-[10px]">{p.module === 'recruiting' ? 'Recruiting' : 'Sales'}</Badge>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600">{p.category || '-'}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {p.roles.map((r, i) => (
                            <Badge key={i} className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                              {r.role}: {fmtINR(r.amount)}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-500">{p.created_by_name || '-'}</td>
                      <td className="px-3 py-3 text-xs text-slate-500">{p.approved_at ? new Date(p.approved_at).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-700">{fmtINR(p.total_share)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200">
                    <td colSpan="6" className="px-4 py-3 text-right font-semibold text-slate-600 text-sm">Total</td>
                    <td className="px-4 py-3 text-right font-bold text-lg text-emerald-700">{fmtINR(total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EarningsPage;
